/**
 * Deal Form Component - Production-grade Create/Edit Deal
 * Multi-section form with validation, default values, and clean UX
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Building,
  MapPin,
  CurrencyEur,
  Wrench,
  Bank,
  SignOut as ExitIcon,
  Users,
  X,
  FloppyDisk,
  ArrowsClockwise,
  CaretRight,
  CaretLeft,
  Info,
  CheckCircle,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { formatCurrency, formatPricePerSqm } from '../../utils/formatters';

// Form sections
const FORM_SECTIONS = [
  { id: 'property', label: 'Property Details', icon: Building },
  { id: 'acquisition', label: 'Acquisition', icon: CurrencyEur },
  { id: 'renovation', label: 'Renovation', icon: Wrench },
  { id: 'financing', label: 'Financing', icon: Bank },
  { id: 'exit', label: 'Exit Strategy', icon: ExitIcon },
  { id: 'partners', label: 'Partners & Notes', icon: Users },
];

// Paris arrondissements
const ARRONDISSEMENTS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th'
];

// Property types
const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'studio', label: 'Studio' },
  { value: 'duplex', label: 'Duplex' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'loft', label: 'Loft' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'townhouse', label: 'Townhouse' },
];

// Condition levels
const CONDITION_LEVELS = [
  { value: 'new', label: 'New' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'renovation_needed', label: 'Renovation Needed' },
];

// Occupancy status
const OCCUPANCY_STATUS = [
  { value: 'vacant', label: 'Vacant' },
  { value: 'owner_occupied', label: 'Owner Occupied' },
  { value: 'tenant_occupied', label: 'Tenant Occupied' },
  { value: 'unknown', label: 'Unknown' },
];

// Source types
const SOURCE_TYPES = [
  { value: 'off_market', label: 'Off-Market' },
  { value: 'broker', label: 'Broker' },
  { value: 'notary', label: 'Notary' },
  { value: 'direct_owner', label: 'Direct Owner' },
  { value: 'auction', label: 'Auction' },
  { value: 'other', label: 'Other' },
];

// Renovation types
const RENOVATION_TYPES = [
  { value: 'cosmetic', label: 'Cosmetic', costPerSqm: 300 },
  { value: 'light', label: 'Light', costPerSqm: 600 },
  { value: 'medium', label: 'Medium', costPerSqm: 1000 },
  { value: 'heavy', label: 'Heavy', costPerSqm: 1500 },
  { value: 'gut_renovation', label: 'Gut Renovation', costPerSqm: 2000 },
];

// Deal statuses
const DEAL_STATUSES = [
  { value: 'sourcing', label: 'Sourcing' },
  { value: 'initial_screening', label: 'Initial Screening' },
  { value: 'underwriting', label: 'Underwriting' },
  { value: 'offer_made', label: 'Offer Made' },
  { value: 'under_negotiation', label: 'Under Negotiation' },
  { value: 'acquired', label: 'Acquired' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'ready_for_sale', label: 'Ready for Sale' },
  { value: 'sold', label: 'Sold' },
  { value: 'dead', label: 'Dead/Rejected' },
];

// Default form values
const getDefaultFormValues = () => ({
  // Deal info
  deal_name: '',
  status: 'sourcing',
  source_type: 'off_market',
  
  // Property
  property: {
    address: '',
    arrondissement: '',
    postal_code: '',
    property_type: 'apartment',
    surface_sqm: '',
    rooms: '',
    floor: '',
    building_condition: 'good',
    apartment_condition: 'fair',
    occupancy_status: 'vacant',
    has_elevator: false,
    has_parking: false,
    has_balcony: false,
    orientation: '',
    building_year: '',
  },
  
  // Acquisition
  acquisition: {
    asking_price: '',
    target_purchase_price: '',
    accepted_purchase_price: '',
    notary_costs_percent: 7.5,
    agency_fee_percent: 0,
    acquisition_tax: 0,
    other_acquisition_costs: 0,
  },
  
  // Renovation
  renovation: {
    renovation_type: 'medium',
    renovation_budget: '',
    renovation_budget_per_sqm: '',
    contingency_percent: 10,
    design_styling_budget: 0,
    project_management_fee: 0,
    planned_start_date: '',
    planned_end_date: '',
    planned_duration_days: '',
  },
  
  // Financing
  financing: {
    equity_contribution: '',
    debt_amount: 0,
    interest_rate_annual: 0,
    financing_fees: 0,
    holding_period_months: 12,
  },
  
  // Exit
  exit_assumptions: {
    target_sale_price: '',
    target_sale_price_per_sqm: '',
    expected_sales_fee_percent: 3.0,
    expected_taxes: 0,
    listing_price: '',
    minimum_acceptable_price: '',
  },
  
  // Partner & Notes
  assigned_partner_id: '',
  sourced_by_id: '',
  target_roi_percent: 20,
  notes: '',
  tags: [],
});

// Validation rules
const validateForm = (values) => {
  const errors = {};
  
  // Required fields
  if (!values.deal_name?.trim()) {
    errors.deal_name = 'Deal name is required';
  }
  
  if (!values.property?.address?.trim()) {
    errors['property.address'] = 'Address is required';
  }
  
  if (!values.property?.arrondissement) {
    errors['property.arrondissement'] = 'Arrondissement is required';
  }
  
  if (!values.property?.surface_sqm || values.property.surface_sqm <= 0) {
    errors['property.surface_sqm'] = 'Valid surface area is required';
  }
  
  if (!values.acquisition?.asking_price || values.acquisition.asking_price <= 0) {
    errors['acquisition.asking_price'] = 'Asking price is required';
  }
  
  if (!values.acquisition?.target_purchase_price || values.acquisition.target_purchase_price <= 0) {
    errors['acquisition.target_purchase_price'] = 'Target purchase price is required';
  }
  
  if (!values.renovation?.renovation_budget || values.renovation.renovation_budget < 0) {
    errors['renovation.renovation_budget'] = 'Renovation budget is required';
  }
  
  if (!values.financing?.equity_contribution || values.financing.equity_contribution <= 0) {
    errors['financing.equity_contribution'] = 'Equity contribution is required';
  }
  
  if (!values.exit_assumptions?.target_sale_price || values.exit_assumptions.target_sale_price <= 0) {
    errors['exit_assumptions.target_sale_price'] = 'Target sale price is required';
  }
  
  // Logical validations
  if (values.acquisition?.target_purchase_price > values.acquisition?.asking_price * 1.1) {
    errors['acquisition.target_purchase_price'] = 'Target price seems unusually high vs asking price';
  }
  
  if (values.exit_assumptions?.target_sale_price < values.acquisition?.target_purchase_price) {
    errors['exit_assumptions.target_sale_price'] = 'Exit price should be higher than purchase price';
  }
  
  return errors;
};

export const DealForm = ({
  initialData = null,
  partners = [],
  onSubmit,
  onCancel,
  isLoading = false,
  mode = 'create', // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState(getDefaultFormValues());
  const [activeSection, setActiveSection] = useState('property');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [tagInput, setTagInput] = useState('');

  // Initialize form with existing data for edit mode
  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData(prev => ({
        ...prev,
        deal_name: initialData.deal_name || '',
        status: initialData.status || 'sourcing',
        source_type: initialData.source_type || 'off_market',
        property: {
          ...prev.property,
          ...initialData.property,
          surface_sqm: initialData.property?.surface_sqm || '',
          rooms: initialData.property?.rooms || '',
          floor: initialData.property?.floor || '',
          building_year: initialData.property?.building_year || '',
        },
        acquisition: {
          ...prev.acquisition,
          ...initialData.acquisition,
          asking_price: initialData.acquisition?.asking_price || '',
          target_purchase_price: initialData.acquisition?.target_purchase_price || '',
          accepted_purchase_price: initialData.acquisition?.accepted_purchase_price || '',
        },
        renovation: {
          ...prev.renovation,
          ...initialData.renovation,
          renovation_budget: initialData.renovation?.renovation_budget || '',
          renovation_budget_per_sqm: initialData.renovation?.renovation_budget_per_sqm || '',
          design_styling_budget: initialData.renovation?.design_styling_budget || 0,
          project_management_fee: initialData.renovation?.project_management_fee || 0,
        },
        financing: {
          ...prev.financing,
          ...initialData.financing,
          equity_contribution: initialData.financing?.equity_contribution || '',
        },
        exit_assumptions: {
          ...prev.exit_assumptions,
          ...initialData.exit_assumptions,
          target_sale_price: initialData.exit_assumptions?.target_sale_price || '',
          target_sale_price_per_sqm: initialData.exit_assumptions?.target_sale_price_per_sqm || '',
          listing_price: initialData.exit_assumptions?.listing_price || '',
          minimum_acceptable_price: initialData.exit_assumptions?.minimum_acceptable_price || '',
        },
        assigned_partner_id: initialData.assigned_partner_id || '',
        sourced_by_id: initialData.sourced_by_id || '',
        target_roi_percent: initialData.target_roi_percent || 20,
        notes: initialData.notes || '',
        tags: initialData.tags || [],
      }));
    }
  }, [initialData, mode]);

  // Helper to update nested fields
  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setTouched(prev => ({ ...prev, [`${section}.${field}`]: true }));
  };

  // Helper to update top-level fields
  const updateTopField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Auto-calculate renovation budget from type and surface
  const autoCalculateRenovation = (type, surfaceSqm) => {
    const typeConfig = RENOVATION_TYPES.find(t => t.value === type);
    if (typeConfig && surfaceSqm > 0) {
      const budget = typeConfig.costPerSqm * surfaceSqm;
      setFormData(prev => ({
        ...prev,
        renovation: {
          ...prev.renovation,
          renovation_budget: budget,
          renovation_budget_per_sqm: typeConfig.costPerSqm,
        },
      }));
    }
  };

  // Auto-calculate equity from total project cost
  const calculateEquity = useMemo(() => {
    const purchase = parseFloat(formData.acquisition.target_purchase_price) || 0;
    const notaryPercent = parseFloat(formData.acquisition.notary_costs_percent) || 7.5;
    const notaryCosts = purchase * (notaryPercent / 100);
    const agencyFee = parseFloat(formData.acquisition.agency_fee_percent) || 0;
    const agencyCosts = purchase * (agencyFee / 100);
    const renovationBudget = parseFloat(formData.renovation.renovation_budget) || 0;
    const contingencyPercent = parseFloat(formData.renovation.contingency_percent) || 10;
    const contingency = renovationBudget * (contingencyPercent / 100);
    const designBudget = parseFloat(formData.renovation.design_styling_budget) || 0;
    const pmFee = parseFloat(formData.renovation.project_management_fee) || 0;
    const debt = parseFloat(formData.financing.debt_amount) || 0;
    
    const totalProjectCost = purchase + notaryCosts + agencyCosts + renovationBudget + contingency + designBudget + pmFee;
    const requiredEquity = totalProjectCost - debt;
    
    return { totalProjectCost, requiredEquity };
  }, [formData.acquisition, formData.renovation, formData.financing.debt_amount]);

  // Live calculation preview
  const livePreview = useMemo(() => {
    const surfaceSqm = parseFloat(formData.property.surface_sqm) || 0;
    const purchase = parseFloat(formData.acquisition.target_purchase_price) || 0;
    const renovation = parseFloat(formData.renovation.renovation_budget) || 0;
    const contingencyPercent = parseFloat(formData.renovation.contingency_percent) || 10;
    const contingency = renovation * (contingencyPercent / 100);
    const notaryPercent = parseFloat(formData.acquisition.notary_costs_percent) || 7.5;
    const notary = purchase * (notaryPercent / 100);
    const exitPrice = parseFloat(formData.exit_assumptions.target_sale_price) || 0;
    const salesFeePercent = parseFloat(formData.exit_assumptions.expected_sales_fee_percent) || 3;
    const salesFee = exitPrice * (salesFeePercent / 100);
    
    const totalProject = purchase + notary + renovation + contingency;
    const netProceeds = exitPrice - salesFee;
    const grossProfit = netProceeds - totalProject;
    const roi = totalProject > 0 ? (grossProfit / totalProject) * 100 : 0;
    const costPerSqm = surfaceSqm > 0 ? totalProject / surfaceSqm : 0;
    const exitPerSqm = surfaceSqm > 0 ? exitPrice / surfaceSqm : 0;
    
    return {
      totalProject,
      grossProfit,
      roi,
      costPerSqm,
      exitPerSqm,
      breakEven: totalProject / (1 - salesFeePercent / 100),
    };
  }, [formData]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      // Find first error section and go there
      const firstError = Object.keys(validationErrors)[0];
      const section = firstError.split('.')[0];
      if (FORM_SECTIONS.some(s => s.id === section)) {
        setActiveSection(section);
      }
      toast.error('Please fix the validation errors');
      return;
    }
    
    // Prepare data for API - ensure numeric fields are numbers
    const preparedData = {
      deal_name: formData.deal_name,
      status: formData.status,
      source_type: formData.source_type,
      property: {
        ...formData.property,
        surface_sqm: parseFloat(formData.property.surface_sqm) || 0,
        rooms: parseInt(formData.property.rooms) || 1,
        floor: parseInt(formData.property.floor) || 0,
        building_year: formData.property.building_year ? parseInt(formData.property.building_year) : null,
      },
      acquisition: {
        ...formData.acquisition,
        asking_price: parseFloat(formData.acquisition.asking_price) || 0,
        target_purchase_price: parseFloat(formData.acquisition.target_purchase_price) || 0,
        accepted_purchase_price: formData.acquisition.accepted_purchase_price 
          ? parseFloat(formData.acquisition.accepted_purchase_price) 
          : null,
        notary_costs_percent: parseFloat(formData.acquisition.notary_costs_percent) || 7.5,
        agency_fee_percent: parseFloat(formData.acquisition.agency_fee_percent) || 0,
        acquisition_tax: parseFloat(formData.acquisition.acquisition_tax) || 0,
        other_acquisition_costs: parseFloat(formData.acquisition.other_acquisition_costs) || 0,
      },
      renovation: {
        ...formData.renovation,
        renovation_budget: parseFloat(formData.renovation.renovation_budget) || 0,
        renovation_budget_per_sqm: parseFloat(formData.renovation.renovation_budget_per_sqm) || null,
        contingency_percent: parseFloat(formData.renovation.contingency_percent) || 10,
        design_styling_budget: parseFloat(formData.renovation.design_styling_budget) || 0,
        project_management_fee: parseFloat(formData.renovation.project_management_fee) || 0,
        planned_start_date: formData.renovation.planned_start_date || null,
        planned_end_date: formData.renovation.planned_end_date || null,
        planned_duration_days: formData.renovation.planned_duration_days 
          ? parseInt(formData.renovation.planned_duration_days) 
          : null,
      },
      financing: {
        ...formData.financing,
        equity_contribution: parseFloat(formData.financing.equity_contribution) || calculateEquity.requiredEquity,
        debt_amount: parseFloat(formData.financing.debt_amount) || 0,
        interest_rate_annual: parseFloat(formData.financing.interest_rate_annual) || 0,
        financing_fees: parseFloat(formData.financing.financing_fees) || 0,
        holding_period_months: parseInt(formData.financing.holding_period_months) || 12,
      },
      exit_assumptions: {
        ...formData.exit_assumptions,
        target_sale_price: parseFloat(formData.exit_assumptions.target_sale_price) || 0,
        target_sale_price_per_sqm: parseFloat(formData.exit_assumptions.target_sale_price_per_sqm) || null,
        expected_sales_fee_percent: parseFloat(formData.exit_assumptions.expected_sales_fee_percent) || 3.0,
        expected_taxes: parseFloat(formData.exit_assumptions.expected_taxes) || 0,
        listing_price: formData.exit_assumptions.listing_price 
          ? parseFloat(formData.exit_assumptions.listing_price) 
          : null,
        minimum_acceptable_price: formData.exit_assumptions.minimum_acceptable_price 
          ? parseFloat(formData.exit_assumptions.minimum_acceptable_price) 
          : null,
      },
      assigned_partner_id: formData.assigned_partner_id || null,
      sourced_by_id: formData.sourced_by_id || null,
      target_roi_percent: parseFloat(formData.target_roi_percent) || 20,
      notes: formData.notes || null,
      tags: formData.tags || [],
    };
    
    onSubmit(preparedData);
  };

  // Add tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  // Remove tag
  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  // Navigate sections
  const goToNextSection = () => {
    const currentIndex = FORM_SECTIONS.findIndex(s => s.id === activeSection);
    if (currentIndex < FORM_SECTIONS.length - 1) {
      setActiveSection(FORM_SECTIONS[currentIndex + 1].id);
    }
  };

  const goToPrevSection = () => {
    const currentIndex = FORM_SECTIONS.findIndex(s => s.id === activeSection);
    if (currentIndex > 0) {
      setActiveSection(FORM_SECTIONS[currentIndex - 1].id);
    }
  };

  // Check if section has errors
  const sectionHasErrors = (sectionId) => {
    return Object.keys(errors).some(key => key.startsWith(sectionId) || key === sectionId);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="deal-form" className="space-y-4 sm:space-y-6">
      {/* Section Navigation - Scrollable on mobile */}
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-2 border-b -mx-3 px-3 sm:mx-0 sm:px-0">
        {FORM_SECTIONS.map((section, idx) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          const hasError = sectionHasErrors(section.id);
          
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              data-testid={`section-tab-${section.id}`}
              className={cn(
                'flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0',
                isActive 
                  ? 'bg-[#3A4A40] text-white' 
                  : 'bg-secondary/50 hover:bg-secondary text-muted-foreground',
                hasError && 'ring-2 ring-red-500'
              )}
            >
              <Icon size={16} className="sm:hidden" />
              <Icon size={18} className="hidden sm:block" />
              <span className="hidden sm:inline">{section.label}</span>
              <span className="sm:hidden">{section.label.split(' ')[0]}</span>
              {hasError && <Warning size={14} className="text-red-500" />}
            </button>
          );
        })}
      </div>

      {/* Live Preview Panel - Responsive */}
      <Card className="bg-secondary/30">
        <CardContent className="py-2 sm:py-3">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Project Cost</p>
              <p className="font-bold font-tabular text-sm sm:text-base">{formatCurrency(livePreview.totalProject)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Cost/m²</p>
              <p className="font-bold font-tabular text-sm sm:text-base">{formatPricePerSqm(livePreview.costPerSqm)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Exit/m²</p>
              <p className="font-bold font-tabular text-sm sm:text-base">{formatPricePerSqm(livePreview.exitPerSqm)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Gross Profit</p>
              <p className={cn(
                'font-bold font-tabular text-sm sm:text-base',
                livePreview.grossProfit >= 0 ? 'text-emerald-600' : 'text-red-600'
              )}>
                {formatCurrency(livePreview.grossProfit)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Expected ROI</p>
              <p className={cn(
                'font-bold font-tabular text-sm sm:text-base',
                livePreview.roi >= 20 ? 'text-emerald-600' :
                livePreview.roi >= 10 ? 'text-amber-600' : 'text-red-600'
              )}>
                {livePreview.roi.toFixed(1)}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Break-Even</p>
              <p className="font-bold font-tabular text-sm sm:text-base">{formatCurrency(livePreview.breakEven)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Sections */}
      <div className="min-h-[400px]">
        {/* Property Details Section */}
        {activeSection === 'property' && (
          <Card data-testid="section-property">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building size={20} />
                Property Details
              </CardTitle>
              <CardDescription>Enter the property information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Deal Name & Status Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="deal_name">Deal Name *</Label>
                  <Input
                    id="deal_name"
                    data-testid="input-deal-name"
                    value={formData.deal_name}
                    onChange={(e) => updateTopField('deal_name', e.target.value)}
                    placeholder="e.g., 15 Rue de Rivoli - 4th Arr"
                    className={errors.deal_name ? 'border-red-500' : ''}
                  />
                  {errors.deal_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.deal_name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="status">Pipeline Stage</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => updateTopField('status', v)}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEAL_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    data-testid="input-address"
                    value={formData.property.address}
                    onChange={(e) => updateField('property', 'address', e.target.value)}
                    placeholder="e.g., 15 Rue de Rivoli"
                    className={errors['property.address'] ? 'border-red-500' : ''}
                  />
                  {errors['property.address'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['property.address']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    data-testid="input-postal-code"
                    value={formData.property.postal_code}
                    onChange={(e) => updateField('property', 'postal_code', e.target.value)}
                    placeholder="75004"
                  />
                </div>
              </div>

              {/* Location Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="arrondissement">Arrondissement *</Label>
                  <Select
                    value={formData.property.arrondissement}
                    onValueChange={(v) => updateField('property', 'arrondissement', v)}
                  >
                    <SelectTrigger 
                      data-testid="select-arrondissement"
                      className={errors['property.arrondissement'] ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ARRONDISSEMENTS.map(arr => (
                        <SelectItem key={arr} value={arr}>{arr} Arr.</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors['property.arrondissement'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['property.arrondissement']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="property_type">Property Type</Label>
                  <Select
                    value={formData.property.property_type}
                    onValueChange={(v) => updateField('property', 'property_type', v)}
                  >
                    <SelectTrigger data-testid="select-property-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="source_type">Deal Source</Label>
                  <Select
                    value={formData.source_type}
                    onValueChange={(v) => updateTopField('source_type', v)}
                  >
                    <SelectTrigger data-testid="select-source-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_TYPES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Property Specs Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="surface_sqm">Surface (m²) *</Label>
                  <Input
                    id="surface_sqm"
                    type="number"
                    data-testid="input-surface"
                    value={formData.property.surface_sqm}
                    onChange={(e) => {
                      updateField('property', 'surface_sqm', e.target.value);
                      // Auto-calculate renovation if type is set
                      if (formData.renovation.renovation_type) {
                        autoCalculateRenovation(formData.renovation.renovation_type, parseFloat(e.target.value) || 0);
                      }
                    }}
                    placeholder="65"
                    className={errors['property.surface_sqm'] ? 'border-red-500' : ''}
                  />
                  {errors['property.surface_sqm'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['property.surface_sqm']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="rooms">Rooms</Label>
                  <Input
                    id="rooms"
                    type="number"
                    data-testid="input-rooms"
                    value={formData.property.rooms}
                    onChange={(e) => updateField('property', 'rooms', e.target.value)}
                    placeholder="3"
                  />
                </div>
                <div>
                  <Label htmlFor="floor">Floor</Label>
                  <Input
                    id="floor"
                    type="number"
                    data-testid="input-floor"
                    value={formData.property.floor}
                    onChange={(e) => updateField('property', 'floor', e.target.value)}
                    placeholder="4"
                  />
                </div>
                <div>
                  <Label htmlFor="building_year">Building Year</Label>
                  <Input
                    id="building_year"
                    type="number"
                    data-testid="input-building-year"
                    value={formData.property.building_year}
                    onChange={(e) => updateField('property', 'building_year', e.target.value)}
                    placeholder="1890"
                  />
                </div>
              </div>

              {/* Condition Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="building_condition">Building Condition</Label>
                  <Select
                    value={formData.property.building_condition}
                    onValueChange={(v) => updateField('property', 'building_condition', v)}
                  >
                    <SelectTrigger data-testid="select-building-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_LEVELS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="apartment_condition">Apartment Condition</Label>
                  <Select
                    value={formData.property.apartment_condition}
                    onValueChange={(v) => updateField('property', 'apartment_condition', v)}
                  >
                    <SelectTrigger data-testid="select-apartment-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_LEVELS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="occupancy_status">Occupancy Status</Label>
                  <Select
                    value={formData.property.occupancy_status}
                    onValueChange={(v) => updateField('property', 'occupancy_status', v)}
                  >
                    <SelectTrigger data-testid="select-occupancy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCUPANCY_STATUS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Amenities Row */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has_elevator"
                    data-testid="checkbox-elevator"
                    checked={formData.property.has_elevator}
                    onCheckedChange={(v) => updateField('property', 'has_elevator', v)}
                  />
                  <Label htmlFor="has_elevator" className="cursor-pointer">Elevator</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has_parking"
                    data-testid="checkbox-parking"
                    checked={formData.property.has_parking}
                    onCheckedChange={(v) => updateField('property', 'has_parking', v)}
                  />
                  <Label htmlFor="has_parking" className="cursor-pointer">Parking</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has_balcony"
                    data-testid="checkbox-balcony"
                    checked={formData.property.has_balcony}
                    onCheckedChange={(v) => updateField('property', 'has_balcony', v)}
                  />
                  <Label htmlFor="has_balcony" className="cursor-pointer">Balcony</Label>
                </div>
              </div>

              <div>
                <Label htmlFor="orientation">Orientation</Label>
                <Input
                  id="orientation"
                  data-testid="input-orientation"
                  value={formData.property.orientation}
                  onChange={(e) => updateField('property', 'orientation', e.target.value)}
                  placeholder="e.g., South-facing, Courtyard view"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Acquisition Section */}
        {activeSection === 'acquisition' && (
          <Card data-testid="section-acquisition">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CurrencyEur size={20} />
                Acquisition Assumptions
              </CardTitle>
              <CardDescription>Define purchase price and acquisition costs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Prices Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="asking_price">Asking Price (€) *</Label>
                  <Input
                    id="asking_price"
                    type="number"
                    data-testid="input-asking-price"
                    value={formData.acquisition.asking_price}
                    onChange={(e) => updateField('acquisition', 'asking_price', e.target.value)}
                    placeholder="500000"
                    className={errors['acquisition.asking_price'] ? 'border-red-500' : ''}
                  />
                  {errors['acquisition.asking_price'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['acquisition.asking_price']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="target_purchase_price">Target Purchase Price (€) *</Label>
                  <Input
                    id="target_purchase_price"
                    type="number"
                    data-testid="input-target-price"
                    value={formData.acquisition.target_purchase_price}
                    onChange={(e) => updateField('acquisition', 'target_purchase_price', e.target.value)}
                    placeholder="450000"
                    className={errors['acquisition.target_purchase_price'] ? 'border-red-500' : ''}
                  />
                  {errors['acquisition.target_purchase_price'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['acquisition.target_purchase_price']}</p>
                  )}
                  {formData.acquisition.asking_price && formData.acquisition.target_purchase_price && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {((1 - formData.acquisition.target_purchase_price / formData.acquisition.asking_price) * 100).toFixed(1)}% below asking
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="accepted_purchase_price">Accepted Price (€)</Label>
                  <Input
                    id="accepted_purchase_price"
                    type="number"
                    data-testid="input-accepted-price"
                    value={formData.acquisition.accepted_purchase_price}
                    onChange={(e) => updateField('acquisition', 'accepted_purchase_price', e.target.value)}
                    placeholder="Leave empty if not yet negotiated"
                  />
                </div>
              </div>

              {/* Costs Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="notary_costs_percent">Notary Costs (%)</Label>
                  <Input
                    id="notary_costs_percent"
                    type="number"
                    step="0.1"
                    data-testid="input-notary-percent"
                    value={formData.acquisition.notary_costs_percent}
                    onChange={(e) => updateField('acquisition', 'notary_costs_percent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    = {formatCurrency((formData.acquisition.target_purchase_price || 0) * (formData.acquisition.notary_costs_percent / 100))}
                  </p>
                </div>
                <div>
                  <Label htmlFor="agency_fee_percent">Agency Fee (%)</Label>
                  <Input
                    id="agency_fee_percent"
                    type="number"
                    step="0.1"
                    data-testid="input-agency-percent"
                    value={formData.acquisition.agency_fee_percent}
                    onChange={(e) => updateField('acquisition', 'agency_fee_percent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    = {formatCurrency((formData.acquisition.target_purchase_price || 0) * (formData.acquisition.agency_fee_percent / 100))}
                  </p>
                </div>
                <div>
                  <Label htmlFor="acquisition_tax">Acquisition Tax (€)</Label>
                  <Input
                    id="acquisition_tax"
                    type="number"
                    data-testid="input-acquisition-tax"
                    value={formData.acquisition.acquisition_tax}
                    onChange={(e) => updateField('acquisition', 'acquisition_tax', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="other_acquisition_costs">Other Costs (€)</Label>
                  <Input
                    id="other_acquisition_costs"
                    type="number"
                    data-testid="input-other-costs"
                    value={formData.acquisition.other_acquisition_costs}
                    onChange={(e) => updateField('acquisition', 'other_acquisition_costs', e.target.value)}
                  />
                </div>
              </div>

              {/* Total Acquisition Summary */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Acquisition Cost</span>
                  <span className="text-lg font-bold font-tabular">
                    {formatCurrency(
                      (parseFloat(formData.acquisition.target_purchase_price) || 0) +
                      ((parseFloat(formData.acquisition.target_purchase_price) || 0) * (parseFloat(formData.acquisition.notary_costs_percent) || 0) / 100) +
                      ((parseFloat(formData.acquisition.target_purchase_price) || 0) * (parseFloat(formData.acquisition.agency_fee_percent) || 0) / 100) +
                      (parseFloat(formData.acquisition.acquisition_tax) || 0) +
                      (parseFloat(formData.acquisition.other_acquisition_costs) || 0)
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renovation Section */}
        {activeSection === 'renovation' && (
          <Card data-testid="section-renovation">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench size={20} />
                Renovation Assumptions
              </CardTitle>
              <CardDescription>Define renovation scope and budget</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Renovation Type & Budget */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="renovation_type">Renovation Type</Label>
                  <Select
                    value={formData.renovation.renovation_type}
                    onValueChange={(v) => {
                      updateField('renovation', 'renovation_type', v);
                      const surfaceSqm = parseFloat(formData.property.surface_sqm) || 0;
                      if (surfaceSqm > 0) {
                        autoCalculateRenovation(v, surfaceSqm);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="select-renovation-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RENOVATION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label} (~{formatPricePerSqm(t.costPerSqm)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="renovation_budget">Renovation Budget (€) *</Label>
                  <Input
                    id="renovation_budget"
                    type="number"
                    data-testid="input-renovation-budget"
                    value={formData.renovation.renovation_budget}
                    onChange={(e) => updateField('renovation', 'renovation_budget', e.target.value)}
                    placeholder="65000"
                    className={errors['renovation.renovation_budget'] ? 'border-red-500' : ''}
                  />
                  {errors['renovation.renovation_budget'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['renovation.renovation_budget']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="contingency_percent">Contingency (%)</Label>
                  <Input
                    id="contingency_percent"
                    type="number"
                    step="0.5"
                    data-testid="input-contingency"
                    value={formData.renovation.contingency_percent}
                    onChange={(e) => updateField('renovation', 'contingency_percent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    = {formatCurrency((formData.renovation.renovation_budget || 0) * (formData.renovation.contingency_percent / 100))}
                  </p>
                </div>
              </div>

              {/* Additional Costs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="design_styling_budget">Design & Styling Budget (€)</Label>
                  <Input
                    id="design_styling_budget"
                    type="number"
                    data-testid="input-design-budget"
                    value={formData.renovation.design_styling_budget}
                    onChange={(e) => updateField('renovation', 'design_styling_budget', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="project_management_fee">Project Management Fee (€)</Label>
                  <Input
                    id="project_management_fee"
                    type="number"
                    data-testid="input-pm-fee"
                    value={formData.renovation.project_management_fee}
                    onChange={(e) => updateField('renovation', 'project_management_fee', e.target.value)}
                  />
                </div>
              </div>

              {/* Timeline */}
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="planned_start_date">Planned Start Date</Label>
                  <Input
                    id="planned_start_date"
                    type="date"
                    data-testid="input-reno-start"
                    value={formData.renovation.planned_start_date}
                    onChange={(e) => updateField('renovation', 'planned_start_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="planned_end_date">Planned End Date</Label>
                  <Input
                    id="planned_end_date"
                    type="date"
                    data-testid="input-reno-end"
                    value={formData.renovation.planned_end_date}
                    onChange={(e) => updateField('renovation', 'planned_end_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="planned_duration_days">Duration (days)</Label>
                  <Input
                    id="planned_duration_days"
                    type="number"
                    data-testid="input-reno-duration"
                    value={formData.renovation.planned_duration_days}
                    onChange={(e) => updateField('renovation', 'planned_duration_days', e.target.value)}
                    placeholder="90"
                  />
                </div>
              </div>

              {/* Total Renovation Summary */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Renovation Budget (incl. contingency)</span>
                  <span className="text-lg font-bold font-tabular">
                    {formatCurrency(
                      (parseFloat(formData.renovation.renovation_budget) || 0) *
                      (1 + (parseFloat(formData.renovation.contingency_percent) || 0) / 100) +
                      (parseFloat(formData.renovation.design_styling_budget) || 0) +
                      (parseFloat(formData.renovation.project_management_fee) || 0)
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financing Section */}
        {activeSection === 'financing' && (
          <Card data-testid="section-financing">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bank size={20} />
                Financing Assumptions
              </CardTitle>
              <CardDescription>Define equity and debt structure</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="equity_contribution">Equity Contribution (€) *</Label>
                  <Input
                    id="equity_contribution"
                    type="number"
                    data-testid="input-equity"
                    value={formData.financing.equity_contribution}
                    onChange={(e) => updateField('financing', 'equity_contribution', e.target.value)}
                    placeholder={calculateEquity.requiredEquity.toFixed(0)}
                    className={errors['financing.equity_contribution'] ? 'border-red-500' : ''}
                  />
                  {errors['financing.equity_contribution'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['financing.equity_contribution']}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Required: {formatCurrency(calculateEquity.requiredEquity)}
                  </p>
                </div>
                <div>
                  <Label htmlFor="debt_amount">Debt Amount (€)</Label>
                  <Input
                    id="debt_amount"
                    type="number"
                    data-testid="input-debt"
                    value={formData.financing.debt_amount}
                    onChange={(e) => updateField('financing', 'debt_amount', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="interest_rate_annual">Annual Interest Rate (%)</Label>
                  <Input
                    id="interest_rate_annual"
                    type="number"
                    step="0.1"
                    data-testid="input-interest-rate"
                    value={formData.financing.interest_rate_annual}
                    onChange={(e) => updateField('financing', 'interest_rate_annual', e.target.value)}
                    placeholder="4.5"
                  />
                </div>
                <div>
                  <Label htmlFor="financing_fees">Financing Fees (€)</Label>
                  <Input
                    id="financing_fees"
                    type="number"
                    data-testid="input-financing-fees"
                    value={formData.financing.financing_fees}
                    onChange={(e) => updateField('financing', 'financing_fees', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="holding_period_months">Holding Period (months)</Label>
                  <Input
                    id="holding_period_months"
                    type="number"
                    data-testid="input-holding-period"
                    value={formData.financing.holding_period_months}
                    onChange={(e) => updateField('financing', 'holding_period_months', e.target.value)}
                    placeholder="12"
                  />
                </div>
              </div>

              {/* Interest Cost Preview */}
              {formData.financing.debt_amount > 0 && formData.financing.interest_rate_annual > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Info size={16} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Interest Cost Preview</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Monthly Interest</p>
                      <p className="font-bold font-tabular">
                        {formatCurrency(
                          (parseFloat(formData.financing.debt_amount) || 0) *
                          ((parseFloat(formData.financing.interest_rate_annual) || 0) / 100 / 12)
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Interest ({formData.financing.holding_period_months} mo)</p>
                      <p className="font-bold font-tabular text-red-600">
                        {formatCurrency(
                          (parseFloat(formData.financing.debt_amount) || 0) *
                          ((parseFloat(formData.financing.interest_rate_annual) || 0) / 100 / 12) *
                          (parseInt(formData.financing.holding_period_months) || 12)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Exit Section */}
        {activeSection === 'exit' && (
          <Card data-testid="section-exit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExitIcon size={20} />
                Exit Strategy
              </CardTitle>
              <CardDescription>Define sale assumptions and targets</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="target_sale_price">Target Sale Price (€) *</Label>
                  <Input
                    id="target_sale_price"
                    type="number"
                    data-testid="input-exit-price"
                    value={formData.exit_assumptions.target_sale_price}
                    onChange={(e) => {
                      updateField('exit_assumptions', 'target_sale_price', e.target.value);
                      const surfaceSqm = parseFloat(formData.property.surface_sqm) || 0;
                      if (surfaceSqm > 0) {
                        const pricePerSqm = parseFloat(e.target.value) / surfaceSqm;
                        updateField('exit_assumptions', 'target_sale_price_per_sqm', pricePerSqm.toFixed(0));
                      }
                    }}
                    placeholder="700000"
                    className={errors['exit_assumptions.target_sale_price'] ? 'border-red-500' : ''}
                  />
                  {errors['exit_assumptions.target_sale_price'] && (
                    <p className="text-xs text-red-500 mt-1">{errors['exit_assumptions.target_sale_price']}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="expected_sales_fee_percent">Sales Fee (%)</Label>
                  <Input
                    id="expected_sales_fee_percent"
                    type="number"
                    step="0.1"
                    data-testid="input-sales-fee"
                    value={formData.exit_assumptions.expected_sales_fee_percent}
                    onChange={(e) => updateField('exit_assumptions', 'expected_sales_fee_percent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    = {formatCurrency((formData.exit_assumptions.target_sale_price || 0) * (formData.exit_assumptions.expected_sales_fee_percent / 100))}
                  </p>
                </div>
                <div>
                  <Label htmlFor="expected_taxes">Expected Taxes (€)</Label>
                  <Input
                    id="expected_taxes"
                    type="number"
                    data-testid="input-exit-taxes"
                    value={formData.exit_assumptions.expected_taxes}
                    onChange={(e) => updateField('exit_assumptions', 'expected_taxes', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="listing_price">Listing Price (€)</Label>
                  <Input
                    id="listing_price"
                    type="number"
                    data-testid="input-listing-price"
                    value={formData.exit_assumptions.listing_price}
                    onChange={(e) => updateField('exit_assumptions', 'listing_price', e.target.value)}
                    placeholder="Optional - initial listing price"
                  />
                </div>
                <div>
                  <Label htmlFor="minimum_acceptable_price">Minimum Acceptable Price (€)</Label>
                  <Input
                    id="minimum_acceptable_price"
                    type="number"
                    data-testid="input-min-price"
                    value={formData.exit_assumptions.minimum_acceptable_price}
                    onChange={(e) => updateField('exit_assumptions', 'minimum_acceptable_price', e.target.value)}
                    placeholder="Walk-away price"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="target_roi_percent">Target ROI (%)</Label>
                <Input
                  id="target_roi_percent"
                  type="number"
                  step="1"
                  data-testid="input-target-roi"
                  value={formData.target_roi_percent}
                  onChange={(e) => updateTopField('target_roi_percent', e.target.value)}
                  placeholder="20"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used for max bid calculations and deal scoring
                </p>
              </div>

              {/* Net Proceeds Preview */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Net Proceeds</p>
                    <p className="text-lg font-bold font-tabular">
                      {formatCurrency(
                        (parseFloat(formData.exit_assumptions.target_sale_price) || 0) -
                        ((parseFloat(formData.exit_assumptions.target_sale_price) || 0) * 
                         (parseFloat(formData.exit_assumptions.expected_sales_fee_percent) || 0) / 100) -
                        (parseFloat(formData.exit_assumptions.expected_taxes) || 0)
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Exit Price/m²</p>
                    <p className="text-lg font-bold font-tabular">
                      {formatPricePerSqm(
                        (parseFloat(formData.exit_assumptions.target_sale_price) || 0) /
                        (parseFloat(formData.property.surface_sqm) || 1)
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Partners & Notes Section */}
        {activeSection === 'partners' && (
          <Card data-testid="section-partners">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users size={20} />
                Partners & Notes
              </CardTitle>
              <CardDescription>Assign partners and add notes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sourced_by_id">Sourced By</Label>
                  <Select
                    value={formData.sourced_by_id}
                    onValueChange={(v) => updateTopField('sourced_by_id', v)}
                  >
                    <SelectTrigger data-testid="select-sourcer">
                      <SelectValue placeholder="Select sourcer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {partners
                        .filter(p => p.role === 'sourcer' || p.role === 'broker')
                        .map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.role})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assigned_partner_id">Project Manager</Label>
                  <Select
                    value={formData.assigned_partner_id}
                    onValueChange={(v) => updateTopField('assigned_partner_id', v)}
                  >
                    <SelectTrigger data-testid="select-pm">
                      <SelectValue placeholder="Select project manager..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {partners
                        .filter(p => p.role === 'project_manager' || p.role === 'contractor')
                        .map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.role})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    data-testid="input-tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag and press Enter"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addTag}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}>
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="input-notes"
                  value={formData.notes}
                  onChange={(e) => updateTopField('notes', e.target.value)}
                  placeholder="Add any additional notes about this deal..."
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Section Navigation & Submit */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2">
          {FORM_SECTIONS.findIndex(s => s.id === activeSection) > 0 && (
            <Button type="button" variant="outline" onClick={goToPrevSection}>
              <CaretLeft size={16} className="mr-1" />
              Previous
            </Button>
          )}
          {FORM_SECTIONS.findIndex(s => s.id === activeSection) < FORM_SECTIONS.length - 1 && (
            <Button type="button" variant="outline" onClick={goToNextSection}>
              Next
              <CaretRight size={16} className="ml-1" />
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            <X size={16} className="mr-1" />
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-[#3A4A40] hover:bg-[#2E3B33]"
            disabled={isLoading}
            data-testid="submit-deal-btn"
          >
            {isLoading ? (
              <>
                <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FloppyDisk size={16} className="mr-1" />
                {mode === 'create' ? 'Create Deal' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default DealForm;
